'use client';

import { cloneElement, isValidElement, useId, type ReactNode } from 'react';

import { cn } from '../../lib/cn';

/** Primitive condivise per i form di dettaglio record (schede "Dettagli" di CER / Configurazione / Socio). */

const detailInputCls =
	'w-full rounded-lg border border-border bg-background px-3 py-2 text-[13px] text-text-primary focus:border-accent focus:outline-none';

export interface DetailSectionProps {
	/** Titolo della sezione. */
	title: string;
	/** Testo esplicativo mostrato sotto il titolo. */
	hint?: string;
	/** Campi ({@link DetailField} e simili) della sezione. */
	children: ReactNode;
}

/** Sezione con titolo e bordo per raggruppare campi in un form di dettaglio record. */
export function DetailSection({ title, hint, children }: DetailSectionProps) {
	return (
		<div className="rounded-xl border border-border bg-card p-4">
			<div className="mb-1 text-[13px] font-bold uppercase tracking-wider text-text">{title}</div>
			{hint && <p className="mb-3 text-[12px] text-text-muted">{hint}</p>}
			<div className={hint ? 'space-y-3' : 'mt-3 space-y-3'}>{children}</div>
		</div>
	);
}

export interface DetailFieldProps {
	/** Etichetta del campo. */
	label: string;
	/** Testo esplicativo mostrato sotto l'etichetta. */
	hint?: string;
	/** Messaggio di errore di validazione mostrato sotto il controllo. */
	error?: string;
	/**
	 * Avviso mostrato sotto il controllo quando non c'è un errore: il valore si
	 * salva, ma ha una conseguenza da sapere (es. impianto senza tariffa premio).
	 */
	warning?: string;
	/**
	 * Un valore che il campo può prendere con un clic, senza imporlo (es. il CF di
	 * un'impresa uguale alla sua Partita IVA). Mostrato sotto il controllo.
	 */
	suggestion?: { label: string; onApply: () => void };
	/** Controllo del campo (input, select, ecc.). */
	children: ReactNode;
}

/** Etichetta + controllo per un singolo campo di un form di dettaglio record. */
export function DetailField({
	label,
	hint,
	error,
	warning,
	suggestion,
	children,
}: DetailFieldProps) {
	const id = useId();
	const errorId = `${id}-error`;
	const warningId = `${id}-warning`;
	const showWarning = !error && !!warning;
	const control = isValidElement<{
		id?: string;
		'aria-invalid'?: boolean;
		'aria-describedby'?: string;
	}>(children)
		? cloneElement(children, {
				id,
				...(error ? { 'aria-invalid': true, 'aria-describedby': errorId } : {}),
				...(showWarning ? { 'aria-describedby': warningId } : {}),
			})
		: children;
	return (
		<div>
			<label htmlFor={id} className="mb-1 block text-[12px] text-text-muted">
				{label}
			</label>
			{hint && <p className="mb-1 text-[11px] italic text-text-muted">{hint}</p>}
			{control}
			{error && (
				<p id={errorId} className="mt-1 text-[11px] text-danger">
					{error}
				</p>
			)}
			{showWarning && (
				<p id={warningId} className="mt-1 text-[11px] text-warn">
					{warning}
				</p>
			)}
			{suggestion && (
				<button
					type="button"
					onClick={suggestion.onApply}
					className="mt-1 text-[11px] text-accent underline-offset-2 hover:underline"
				>
					{suggestion.label}
				</button>
			)}
		</div>
	);
}

export interface DetailTextInputProps {
	/** Id HTML del campo; iniettato automaticamente da {@link DetailField} per collegare la label. */
	id?: string;
	/** Valore corrente del campo. */
	value: string;
	/** Chiamata a ogni digitazione con il nuovo valore. */
	onChange: (v: string) => void;
	/** Testo segnaposto. */
	placeholder?: string;
	/** Tipo di input HTML. Default: `text`. */
	type?: 'text' | 'email' | 'date' | 'number' | 'password' | 'color';
	/** Se true, forza il testo digitato in maiuscolo. */
	uppercase?: boolean;
	/** Se true, disabilita il campo. */
	disabled?: boolean;
	/** Valore minimo: numero per `type="number"`, `AAAA-MM-GG` per `type="date"`. */
	min?: number | string;
	/** Valore massimo: numero per `type="number"`, `AAAA-MM-GG` per `type="date"`. */
	max?: number | string;
	/** Segnala il campo come non valido; iniettato da {@link DetailField} quando ha un `error`. */
	'aria-invalid'?: boolean;
	/** Id del messaggio di errore; iniettato da {@link DetailField} quando ha un `error`. */
	'aria-describedby'?: string;
}

/** Campo di testo per i form di dettaglio record. */
export function DetailTextInput({
	id,
	value,
	onChange,
	placeholder,
	type = 'text',
	uppercase,
	disabled,
	min,
	max,
	'aria-invalid': ariaInvalid,
	'aria-describedby': ariaDescribedBy,
}: DetailTextInputProps) {
	return (
		<input
			id={id}
			type={type}
			value={value}
			placeholder={placeholder}
			disabled={disabled}
			min={min}
			max={max}
			aria-invalid={ariaInvalid}
			aria-describedby={ariaDescribedBy}
			onChange={(e) => onChange(uppercase ? e.target.value.toUpperCase() : e.target.value)}
			className={cn(
				detailInputCls,
				// Il testo d'aiuto resta com'è scritto: in maiuscolo si legge come un valore.
				uppercase && 'uppercase placeholder:normal-case',
				disabled && 'opacity-50',
				ariaInvalid && 'border-danger',
			)}
		/>
	);
}

export interface DetailTextareaProps {
	/** Id HTML del campo; iniettato automaticamente da {@link DetailField} per collegare la label. */
	id?: string;
	/** Valore corrente del campo. */
	value: string;
	/** Chiamata a ogni digitazione con il nuovo valore. */
	onChange: (v: string) => void;
	/** Testo segnaposto. */
	placeholder?: string;
	/** Numero di righe visibili. Default: 4. */
	rows?: number;
}

/** Area di testo multi-riga per i form di dettaglio record. */
export function DetailTextarea({
	id,
	value,
	onChange,
	placeholder,
	rows = 4,
}: DetailTextareaProps) {
	return (
		<textarea
			id={id}
			value={value}
			placeholder={placeholder}
			rows={rows}
			onChange={(e) => onChange(e.target.value)}
			className={cn(detailInputCls, 'resize-none')}
		/>
	);
}

export interface DetailSelectInputProps {
	/** Id HTML del campo; iniettato automaticamente da {@link DetailField} per collegare la label. */
	id?: string;
	/** Valore selezionato corrente. */
	value: string;
	/** Chiamata alla selezione di una nuova opzione. */
	onChange: (v: string) => void;
	/** Opzioni selezionabili. */
	options: { value: string; label: string }[];
	/**
	 * Testo dell'opzione vuota iniziale. Default: "— seleziona —". `null` la
	 * toglie, per un campo che ha sempre un valore: altrimenti il menu mostra la
	 * stessa voce due volte.
	 */
	placeholder?: string | null;
	/** Blocca il campo, es. durante un salvataggio. */
	disabled?: boolean;
	/**
	 * Nome accessibile, quando il campo vive fuori da una {@link DetailField} e
	 * quindi non ha una `<label>` collegata. Senza, uno screen reader annuncia
	 * solo «menu» — ed è una violazione axe, non una raffinatezza.
	 */
	'aria-label'?: string;
	/** Segnala il campo come non valido; iniettato da {@link DetailField} quando ha un `error`. */
	'aria-invalid'?: boolean;
	/** Id del messaggio di errore; iniettato da {@link DetailField} quando ha un `error`. */
	'aria-describedby'?: string;
}

/** Menu a tendina per i form di dettaglio record. */
export function DetailSelectInput({
	id,
	value,
	onChange,
	options,
	placeholder = '- seleziona -',
	disabled,
	'aria-label': ariaLabel,
	'aria-invalid': ariaInvalid,
	'aria-describedby': ariaDescribedBy,
}: DetailSelectInputProps) {
	return (
		<select
			id={id}
			value={value}
			disabled={disabled}
			aria-label={ariaLabel}
			aria-invalid={ariaInvalid}
			aria-describedby={ariaDescribedBy}
			onChange={(e) => onChange(e.target.value)}
			className={cn(
				detailInputCls,
				ariaInvalid && 'border-danger',
				disabled && 'cursor-not-allowed opacity-60',
			)}
		>
			{placeholder !== null && <option value="">{placeholder}</option>}
			{options.map((o) => (
				<option key={o.value} value={o.value}>
					{o.label}
				</option>
			))}
		</select>
	);
}

export interface DetailToggleProps {
	/** Etichetta del toggle. */
	label: string;
	/** Testo esplicativo mostrato sotto l'etichetta. */
	hint?: string;
	/** Stato corrente (acceso/spento). */
	checked: boolean;
	/** Chiamata al click (nessun parametro: il chiamante inverte lo stato). */
	onChange: () => void;
	/** Interruttore non interagibile (es. richiesta in corso). */
	disabled?: boolean;
}

/** Interruttore on/off con etichetta e nota, per i form di dettaglio record. */
export function DetailToggle({ label, hint, checked, onChange, disabled }: DetailToggleProps) {
	return (
		<div className="flex items-center justify-between rounded-lg border border-border bg-background/50 px-3 py-2">
			<div>
				<div className="text-[13px] font-semibold text-text">{label}</div>
				{hint && <div className="text-[11px] italic text-text-muted">{hint}</div>}
			</div>
			<button
				type="button"
				aria-label={label}
				onClick={onChange}
				disabled={disabled}
				className={cn(
					'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none',
					checked ? 'bg-accent' : 'bg-border',
					disabled && 'cursor-not-allowed opacity-50',
				)}
			>
				<span
					className={cn(
						'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transition-transform',
						checked ? 'translate-x-4' : 'translate-x-0',
					)}
				/>
			</button>
		</div>
	);
}

export interface DetailGrid2Props {
	/** Campi da disporre su due colonne. */
	children: ReactNode;
}

/** Griglia a due colonne per campi numerici compatti. */
export function DetailGrid2({ children }: DetailGrid2Props) {
	return <div className="grid grid-cols-2 gap-3">{children}</div>;
}
